import {
  TICKET_STATUS_BEHAVIOR_LABELS,
  TicketStatusBehavior,
} from '@21k/shared';
import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  createTicketStatus,
  getApiErrorMessage,
  getTicketStatus,
  listTicketStatusOptions,
  updateTicketStatusDefinition,
} from '../../api/client';
import type { TicketStatusRef } from '../../api/types';
import { useAuth } from '../../contexts/AuthContext';
import { canEditPlatformConfig, canWritePlatformConfig } from '../../utils/permissions';
import { PLATFORM_CONFIG } from '../platform-config/paths';

const BEHAVIORS = Object.values(TicketStatusBehavior);

export default function TicketStatusFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { user } = useAuth();

  const [allStatuses, setAllStatuses] = useState<TicketStatusRef[]>([]);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isTerminal, setIsTerminal] = useState(false);
  const [onEnterBehavior, setOnEnterBehavior] = useState<TicketStatusBehavior>(
    TicketStatusBehavior.NONE,
  );
  const [sortOrder, setSortOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [isSystem, setIsSystem] = useState(false);
  const [allowedTargetIds, setAllowedTargetIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canEdit =
    user && (isNew ? canWritePlatformConfig(user) : canEditPlatformConfig(user));

  useEffect(() => {
    listTicketStatusOptions().then(setAllStatuses).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (isNew || !id) return;
    getTicketStatus(id)
      .then((s) => {
        setCode(s.code);
        setName(s.name);
        setDescription(s.description ?? '');
        setIsTerminal(s.isTerminal);
        setOnEnterBehavior(s.onEnterBehavior ?? TicketStatusBehavior.NONE);
        setSortOrder(s.sortOrder);
        setIsActive(s.isActive);
        setIsDefault(Boolean(s.isDefault));
        setIsSystem(s.isSystem);
        setAllowedTargetIds(s.allowedTargetIds);
      })
      .catch((err) => setError(getApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  function toggleTarget(targetId: string) {
    setAllowedTargetIds((prev) =>
      prev.includes(targetId) ? prev.filter((x) => x !== targetId) : [...prev, targetId],
    );
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    setError('');
    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      isTerminal,
      onEnterBehavior,
      sortOrder,
      isActive,
      isDefault,
      allowedTargetIds,
    };
    try {
      if (isNew) {
        await createTicketStatus({
          code: code.trim(),
          ...payload,
          description: description.trim() || undefined,
        });
      } else if (id) {
        await updateTicketStatusDefinition(id, payload);
      }
      navigate(PLATFORM_CONFIG.statuses);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  if (!canEdit) return <div className="alert alert-error">No permission.</div>;
  if (loading) return <div className="loading">Loading…</div>;

  const transitionOptions = allStatuses.filter((s) => s.id !== id);

  return (
    <>
      <div className="page-header">
        <Link to={PLATFORM_CONFIG.statuses} style={{ fontSize: '0.85rem' }}>← Statuses</Link>
        <h1>{isNew ? 'New status' : 'Edit status'}</h1>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      <form className="card card-body" style={{ maxWidth: 640 }} onSubmit={handleSave}>
        <div className="form-group">
          <label>Code</label>
          <input className="form-control" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} required disabled={!isNew} />
        </div>
        <div className="form-group">
          <label>Name</label>
          <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="form-group">
          <label>Description</label>
          <textarea className="form-control" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="form-group">
          <label>On-enter behavior</label>
          <select
            className="form-control"
            value={onEnterBehavior}
            onChange={(e) => setOnEnterBehavior(e.target.value as TicketStatusBehavior)}
          >
            {BEHAVIORS.map((b) => (
              <option key={b} value={b}>{TICKET_STATUS_BEHAVIOR_LABELS[b]}</option>
            ))}
          </select>
        </div>
        <label style={{ display: 'block', marginBottom: '0.75rem' }}>
          <input type="checkbox" checked={isTerminal} onChange={(e) => setIsTerminal(e.target.checked)} /> Terminal status (excluded from open-ticket counts)
        </label>
        <div className="form-group">
          <label>Allowed transitions to</label>
          <p className="form-hint">
            Statuses a ticket can move to after it is already in this status. This status is available on any open ticket while it is active.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            {transitionOptions.map((s) => (
              <label key={s.id}>
                <input
                  type="checkbox"
                  checked={allowedTargetIds.includes(s.id)}
                  onChange={() => toggleTarget(s.id)}
                />{' '}
                {s.name} ({s.code})
              </label>
            ))}
          </div>
        </div>
        <div className="form-group">
          <label>Sort order</label>
          <input type="number" min={0} className="form-control" style={{ maxWidth: 120 }} value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value))} />
        </div>
        <label>
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> Active
        </label>
        <label style={{ display: 'block', marginTop: '0.5rem' }}>
          <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} /> Default status for new tickets
        </label>
        {isSystem && (
          <small style={{ display: 'block', marginTop: '0.35rem', color: 'var(--text-muted)' }}>
            Seeded system status — you can still rename, deactivate, or change the default.
          </small>
        )}
        <button type="submit" className="btn btn-primary" disabled={saving} style={{ marginTop: '1rem' }}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </form>
    </>
  );
}
